import { Check, Link2 } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { SectionHeading, SETTLE_SPRING } from "./SectionHeading";

const ITEMS = [
  { key: "one", tag: "source" },
  { key: "two", tag: "source" },
  { key: "three", tag: "estimate" },
] as const;

const POP = { type: "spring", stiffness: 500, damping: 24 } as const;

/**
 * The self-completing to-do list, live. Items check *themselves* off on a loop
 * and the answer that slides in carries a source/estimate chip — so the same
 * beat shows the delight (it finishes the list) and the proof (it brings
 * receipts, and flags a guess as a guess). Replaces the old static
 * "sounds sure vs can be checked" comparison.
 */
export function SelfCompletingSection() {
  const { t } = useTranslation("home");
  const reduced = useReducedMotion();
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (reduced) return;
    // Reveal one item per tick, hold on "all done" for a few ticks, then loop.
    const id = setInterval(() => setTick((v) => (v + 1) % (ITEMS.length + 3)), 1150);
    return () => clearInterval(id);
  }, [reduced]);

  const revealed = reduced ? ITEMS.length : Math.min(tick, ITEMS.length);
  const allDone = revealed >= ITEMS.length;

  return (
    <section id="what-it-handles" className="scroll-mt-32 px-5 py-20 md:scroll-mt-24 md:py-24">
      <div className="mx-auto max-w-6xl">
        <SectionHeading kicker={t("receipts.kicker")} title={t("receipts.title")} />

        <p className="mx-auto mt-6 max-w-xl text-center font-body text-[16px] leading-relaxed text-home-ink/75">
          {t("receipts.lede")}
        </p>

        {/* The living list */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={SETTLE_SPRING}
          className="mx-auto mt-10 max-w-md rounded-[26px] bg-white p-5 shadow-[0_30px_66px_-32px_rgba(33,31,28,0.5)]"
        >
          <div className="flex items-center justify-between px-1.5 pb-3">
            <p className="font-display text-[16px] font-semibold text-home-ink">
              {t("receipts.cardTitle")}
            </p>
            {allDone ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-green/15 px-2.5 py-1 font-body text-[11px] font-bold text-accent-green">
                <Check className="h-3 w-3" strokeWidth={3} />
                {t("receipts.doneTag")}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 font-body text-[11px] font-semibold text-home-ink/65">
                <span className="h-1.5 w-1.5 rounded-full bg-home-terracotta" />
                {t("receipts.workingTag")}
              </span>
            )}
          </div>

          <ul className="space-y-2">
            {ITEMS.map((item, i) => {
              const state =
                i < revealed ? "done" : i === revealed && !allDone ? "working" : "todo";
              return (
                <Row
                  key={item.key}
                  title={t(`receipts.${item.key}Title`)}
                  answer={t(`receipts.${item.key}Answer`)}
                  tag={item.tag}
                  tagLabel={item.tag === "source" ? t("receipts.sourceTag") : t("receipts.estimateTag")}
                  state={state}
                  reduced={!!reduced}
                />
              );
            })}
          </ul>
        </motion.div>

        {/* Closing line — the second clause carries the marker */}
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ ...SETTLE_SPRING, delay: 0.1 }}
          className="mx-auto mt-10 max-w-2xl text-center font-display text-[clamp(20px,2.4vw,28px)] leading-snug font-semibold tracking-tight text-home-ink"
        >
          {t("receipts.punchLead")}{" "}
          <span className="box-decoration-clone bg-home-yellow px-2 py-0.5">
            {t("receipts.punchMark")}
          </span>
        </motion.p>
      </div>
    </section>
  );
}

function Row({
  title,
  answer,
  tag,
  tagLabel,
  state,
  reduced,
}: {
  title: string;
  answer: string;
  tag: "source" | "estimate";
  tagLabel: string;
  state: "todo" | "working" | "done";
  reduced: boolean;
}) {
  const done = state === "done";
  const working = state === "working";

  return (
    <li className="rounded-2xl bg-home-cream px-3.5 py-3">
      <div className="flex items-center gap-3">
        {/* The checkbox the agent fills in itself */}
        <span
          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
            done
              ? "bg-accent-green/20"
              : working
                ? ""
                : "border-2 border-home-ink/15"
          }`}
        >
          {done ? (
            <motion.span
              initial={reduced ? false : { scale: 0 }}
              animate={{ scale: 1 }}
              transition={POP}
            >
              <Check className="h-3.5 w-3.5 text-accent-green" strokeWidth={3} />
            </motion.span>
          ) : working ? (
            <span className="h-6 w-6 animate-spin rounded-full border-2 border-home-terracotta/25 border-t-home-terracotta" />
          ) : null}
        </span>

        <span
          className={`flex-1 font-body text-[14px] leading-snug font-semibold transition-colors ${
            done ? "text-home-ink" : working ? "text-home-ink/70" : "text-home-ink/65"
          }`}
        >
          {title}
        </span>
      </div>

      {/* The answer slides in when done — and brings its receipt */}
      <motion.div
        initial={false}
        animate={done ? { height: "auto", opacity: 1 } : { height: 0, opacity: 0 }}
        transition={{ duration: reduced ? 0 : 0.35, ease: "easeOut" }}
        className="overflow-hidden"
      >
        <div className="flex items-center justify-between gap-2 pt-2 pl-9">
          <span className="font-body text-[13px] leading-snug text-home-ink/70">{answer}</span>
          {tag === "source" ? (
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-home-sky px-2.5 py-0.5 font-body text-[11px] font-bold text-home-ink">
              <Link2 className="h-3 w-3" strokeWidth={2.5} />
              {tagLabel}
            </span>
          ) : (
            <span className="inline-flex shrink-0 items-center rounded-full bg-home-butter px-2.5 py-0.5 font-body text-[11px] font-bold text-home-ink">
              {tagLabel}
            </span>
          )}
        </div>
      </motion.div>
    </li>
  );
}
